/**
 * Live updates: one EventSource per session. Domain events invalidate the
 * affected queries; progress events patch cached data in place so progress
 * bars move without refetching.
 */
import { useEffect, useState } from 'react';
import { useQueryClient, type InfiniteData, type QueryClient } from '@tanstack/react-query';
import type { AssetDetail, AssetSummary, HandoffDTO, QcRunDTO, TaskDTO } from '@prism/contracts';
import { api, ApiError } from './client';
import { onSessionError } from './session-end';
import { toast } from '../components/Toasts';
import type { LibraryResult } from './hooks';
import { fetchLibraryPage } from './hooks/library';

function patchProgress(qc: QueryClient, assetId: string, step: string, percent: number) {
  qc.setQueriesData<InfiniteData<LibraryResult>>({ queryKey: ['assets'] }, (old) => {
    if (!old) return old;
    return {
      ...old,
      pages: old.pages.map((page) => ({
        ...page,
        items: page.items.map((a: AssetSummary) =>
          a.id === assetId && a.progress ? { ...a, progress: { ...a.progress, step, percent } } : a,
        ),
      })),
    };
  });
  qc.setQueryData<AssetDetail>(['asset', assetId], (old) => {
    if (!old) return old;
    return {
      ...old,
      progress: old.progress ? { ...old.progress, step, percent } : old.progress,
      runs: old.runs.map((r, i) =>
        i === 0 ? { ...r, steps: r.steps.map((s) => (s.step === step ? { ...s, detail: { ...s.detail, percent } } : s)) } : r,
      ),
    };
  });
}

type DomainListener = (event: { type: string; aggregate_id?: string }) => void;
const domainListeners = new Set<DomainListener>();

/** Follow domain events as they arrive (e.g. an open editor's sequence.lock_* events); returns the unsubscribe. */

type ExportRow = { id: string } & Record<string, unknown>;
type ExportList = { items: ExportRow[] };
/**
 * Applies a change to every cached export list: the asset's list ({ items }) and the sequence's, which comes in
 * pages since 1.0.0-rc.3 ({ pages: [{ items }] }).
 */
function patchExports(qc: QueryClient, f: (x: ExportRow) => ExportRow): void {
  qc.setQueriesData<ExportList | { pages: ExportList[]; pageParams: unknown[] }>({ queryKey: ['exports'] }, (old) => {
    if (!old) return old;
    if ('pages' in old) return { ...old, pages: old.pages.map((p) => ({ ...p, items: p.items.map(f) })) };
    return Array.isArray(old.items) ? { ...old, items: old.items.map(f) } : old;
  });
}

/** "Ready on <location>" for a hand-off the signed-in person sent. */
async function announceHandoff(id: string) {
  try {
    const [h, me] = await Promise.all([api<HandoffDTO>(`/api/v1/handoffs/${id}`), api<{ user: { id: string } }>('/api/v1/me')]);
    if (h.createdBy?.id === me.user.id) toast(`“${h.name}” is ready on ${h.location.name}${h.workstationFolder ? `: ${h.workstationFolder}` : ''}`);
  } catch {
    // Not visible to this person, or gone: nothing to say.
  }
}

/** An edited timeline is waiting in _to_prism/ of a hand-off I sent (the edit location watches it, 1.0.0-rc.7). */
async function announceCheckin(id: string) {
  try {
    const [h, me] = await Promise.all([api<HandoffDTO>(`/api/v1/handoffs/${id}`), api<{ user: { id: string } }>('/api/v1/me')]);
    if (h.createdBy?.id === me.user.id) toast(`An edit of “${h.name}” is in _to_prism/ on ${h.location.name}: Check in from the sequence's Exports tab`);
  } catch {
    // Not visible to this person, or gone.
  }
}

/**
 * A task for me (1.0.0-rc.9): assigned to me, or to a role or group I am in (then every member is told), by someone
 * else or by PRISM (a QC review task).
 */
async function announceTask(id: string) {
  try {
    const [t, me] = await Promise.all([api<TaskDTO>(`/api/v1/tasks/${id}`), api<{ user: { id: string } }>('/api/v1/me')]);
    if (t.createdBy?.id === me.user.id || (t.status !== 'open' && t.status !== 'in_progress')) return;
    const forMe = t.assignee?.type === 'user' ? t.assignee.id === me.user.id : t.canTake;
    if (forMe) toast(t.assignee?.type === 'user' ? `New task for you: ${t.title}` : `New task for ${t.assignee!.name}: ${t.title}`);
  } catch {
    // Not visible to this person, or gone.
  }
}

/** A QC run I started failed (1.0.0-rc.8): a note to me, nobody else. */
async function announceQcFailed(assetId: string) {
  try {
    const [q, me, a] = await Promise.all([
      api<{ items: QcRunDTO[] }>(`/api/v1/assets/${assetId}/qc`), api<{ user: { id: string } }>('/api/v1/me'), api<{ title: string }>(`/api/v1/assets/${assetId}`),
    ]);
    const run = q.items.find((r) => r.status === 'failed');
    if (run && run.requestedById === me.user.id) toast(`QC failed for “${a.title}”: ${run.summary?.errors ?? 0} error${run.summary?.errors === 1 ? '' : 's'}. See its QC tab.`, 'error');
  } catch {
    // Not visible to this person, or gone: nothing to say.
  }
}

export function onDomainEvent(l: DomainListener): () => void {
  domainListeners.add(l);
  return () => { domainListeners.delete(l); };
}

type DomainData = { type: string; aggregate_id?: string };
type ProgressData = { assetId?: string; exportId?: string; sequenceId?: string; step: string; percent: number | null; waiting?: boolean; waitingSince?: string | null };

/** What the live stream does with each of its events, apart from the EventSource itself (so it can be tested). */
export interface LiveEventHandlers {
  hello(): void;
  domain(data: DomainData): void;
  progress(d: ProgressData): void;
  dispose(): void;
}

/**
 * The events that change what the Library and an asset's page show (1.0.0-rc.14, #13): asset.* and ingest.* (whose
 * aggregate is the asset). Others (tasks, transfers, automations, hand-offs…) refresh their own lists below.
 */
// 1.4.0: group.* and group_type.* change the Library's rows too (an asset's groups, inherited values, the Grouped view).
export const changesAssets = (type: string): boolean => /^(asset|ingest|group|group_type)\./.test(type);

const tabHidden = () => typeof document !== 'undefined' && document.visibilityState === 'hidden';

/** A Library query with more loaded pages than this refetches only its first page for live events (1.2.7). */
export const LIVE_FULL_REFETCH_PAGES = 5;

export function liveEventHandlers(qc: QueryClient, opts: {
  schedule?: (fn: () => void, ms: number) => number; cancel?: (id: number) => void; isHidden?: () => boolean;
} = {}): LiveEventHandlers {
  const schedule = opts.schedule ?? ((fn, ms) => window.setTimeout(fn, ms));
  const cancel = opts.cancel ?? ((id) => window.clearTimeout(id));
  const isHidden = opts.isHidden ?? tabHidden;
  let pending: number | null = null;
  let assetsChanged = false;
  const touched = new Set<string>();
  // Only queries on screen refetch (an infinite Library query refetches every page it has loaded); in a hidden tab
  // they are only marked stale, and refetch when the tab is shown again (refetchOnWindowFocus).
  const refetchType = () => (isHidden() ? 'none' : 'active');
  // The first page of a deeply scrolled Library, fetched again and swapped in; a page appended meanwhile is kept, and
  // later pages stay as they are (the list drops repeats). A failure only marks the query stale.
  const refreshFirstPage = async (queryKey: readonly unknown[], params: string) => {
    try {
      const fresh = await fetchLibraryPage(params, 0);
      qc.setQueryData<InfiniteData<LibraryResult>>(queryKey, (old) => old && { ...old, pages: [fresh, ...old.pages.slice(1)] });
    } catch {
      void qc.invalidateQueries({ queryKey, exact: true, refetchType: 'none' });
    }
  };
  // 1.2.7: each ['assets', …] query on its own. A Library on screen with more than LIVE_FULL_REFETCH_PAGES pages
  // loaded refetches only its first page (one request per batch of events, not one per page); every other query
  // (fewer pages, not on screen, not a Library list, a hidden tab) is invalidated as before.
  const refreshAssets = () => {
    const type = refetchType();
    for (const query of qc.getQueryCache().findAll({ queryKey: ['assets'] })) {
      const data = query.state.data as Partial<InfiniteData<LibraryResult>> | undefined;
      const params = query.queryKey[1];
      if (type === 'active' && query.isActive() && typeof params === 'string' && (data?.pages?.length ?? 0) > LIVE_FULL_REFETCH_PAGES) {
        void refreshFirstPage(query.queryKey, params);
      } else {
        void qc.invalidateQueries({ queryKey: query.queryKey, exact: true, refetchType: type });
      }
    }
  };
  const flush = () => {
    pending = null;
    if (assetsChanged) {
      refreshAssets();
      // 1.4.0: the Grouped view's levels (queryKeys.grouped, not under assets(): its pages are not Library pages).
      void qc.invalidateQueries({ queryKey: ['assets-grouped'], refetchType: refetchType() });
    }
    assetsChanged = false;
    for (const id of touched) {
      void qc.invalidateQueries({ queryKey: ['asset', id], refetchType: refetchType() });
      void qc.invalidateQueries({ queryKey: ['asset-audit', id], refetchType: refetchType() });
    }
    touched.clear();
  };
  return {
    hello() {
      // Catch up on anything missed while disconnected.
      refreshAssets();
    },
    domain(data) {
      if (changesAssets(data.type)) assetsChanged = true;
      // An asset page refetches for any event about it (a transfer of its copies, its QC…): one query, if it is open.
      if (data.aggregate_id) touched.add(data.aggregate_id);
      for (const l of domainListeners) l(data);
      if (data.type.startsWith('sequence.lock')) void qc.invalidateQueries({ queryKey: ['sequences'] });
      if (data.type === 'storage.availability_changed') {
        void qc.invalidateQueries({ queryKey: ['storage'] });
        void qc.invalidateQueries({ queryKey: ['agents'] });
      }
      if (data.type === 'metadata_model.published') {
        void qc.invalidateQueries({ queryKey: ['schema'] });
        void qc.invalidateQueries({ queryKey: ['admin-model'] });
      }
      if (data.type.startsWith('transfer.')) void qc.invalidateQueries({ queryKey: ['transfers'] });
      if (data.type.startsWith('group.') || data.type.startsWith('group_type.')) {
        void qc.invalidateQueries({ queryKey: ['groups'] });
        void qc.invalidateQueries({ queryKey: ['group-types'] });
      }
      if (data.type.startsWith('restore.')) {
        void qc.invalidateQueries({ queryKey: ['restores'] });
        void qc.invalidateQueries({ queryKey: ['preflight'] });
        void qc.invalidateQueries({ queryKey: ['operations'] });
      }
      if (data.type.startsWith('export.')) void qc.invalidateQueries({ queryKey: ['exports'] });
      // Send to edit (1.0.0-rc.6): the "Sent to edit" lists, and a note when one of mine is ready.
      if (data.type.startsWith('handoff.')) {
        void qc.invalidateQueries({ queryKey: ['handoffs'] });
        if (data.type === 'handoff.ready' && data.aggregate_id) void announceHandoff(data.aggregate_id);
        if (data.type === 'handoff.checkin_available' && data.aggregate_id) void announceCheckin(data.aggregate_id);
      }
      // Check in (1.0.0-rc.7): an open report follows its check-in.
      if (data.type.startsWith('checkin.') && data.aggregate_id) void qc.invalidateQueries({ queryKey: ['checkin', data.aggregate_id] });
      if (data.type.startsWith('automation.') || data.type.startsWith('approval.')) {
        void qc.invalidateQueries({ queryKey: ['automation-runs'] });
        void qc.invalidateQueries({ queryKey: ['approvals'] });
        void qc.invalidateQueries({ queryKey: ['automations'] });
      }
      if (data.type.startsWith('approval.') || data.type.startsWith('share.')) {
        void qc.invalidateQueries({ queryKey: ['approval-notes'] });
        void qc.invalidateQueries({ queryKey: ['shares'] });
        void qc.invalidateQueries({ queryKey: ['tasks', 'summary'] });
      }
      if (['asset.deleted', 'asset.undeleted', 'asset.purged'].includes(data.type)) void qc.invalidateQueries({ queryKey: ['recycle-bin'] });
      if (data.type === 'asset.segments_changed') void qc.invalidateQueries({ queryKey: ['segments', data.aggregate_id] });
      if (data.type === 'strata.changed') void qc.invalidateQueries({ queryKey: ['strata'] });
      if (data.type === 'asset.attachments_changed') void qc.invalidateQueries({ queryKey: ['attachments', data.aggregate_id] });
      if (data.type === 'watch_folder.changed' || data.type === 'asset.watch_ingested') void qc.invalidateQueries({ queryKey: ['watch-folders'] });
      if (data.type === 'storage.location_changed') void qc.invalidateQueries({ queryKey: ['storage'] });
      if (data.type === 'asset.subtitles_changed') void qc.invalidateQueries({ queryKey: ['subtitles'] });
      // Automated QC (1.0.0-rc.8): the asset's QC tab and badge, and a note when a run I started failed.
      if (data.type === 'asset.qc_changed') void qc.invalidateQueries({ queryKey: ['qc'] });
      // 1.2.0: a master added (or replaced) on an asset; the asset and the Library follow as for any asset event.
      if (data.type === 'asset.master_added' || data.type === 'asset.masters_changed') void qc.invalidateQueries({ queryKey: ['masters', data.aggregate_id] });
      if (data.type === 'qc.failed' && data.aggregate_id) void announceQcFailed(data.aggregate_id);
      // Tasks (1.0.0-rc.9): the badge and lists follow, and a note when one is for me (or my role or group).
      if (data.type.startsWith('task.')) {
        void qc.invalidateQueries({ queryKey: ['tasks'] });
        if (data.type === 'task.assigned' && data.aggregate_id) void announceTask(data.aggregate_id);
      }
      pending ??= schedule(flush, 250);
    },
    progress(d) {
      const any = d as unknown as { automationRunId?: string };
      if (any.automationRunId !== undefined) {
        void qc.invalidateQueries({ queryKey: ['automation-run', any.automationRunId] });
        void qc.invalidateQueries({ queryKey: ['automation-runs'] });
        return;
      }
      if (d.exportId && d.waiting !== undefined) {
        // Queued (0.95.0): the export's next step waits for a free worker, or a worker took it.
        patchExports(qc, (x) => (x.id === d.exportId ? { ...x, waiting: Boolean(d.waiting), waitingSince: d.waitingSince ?? null } : x));
        return;
      }
      if (d.exportId) {
        patchExports(qc, (x) => (x.id === d.exportId ? { ...x, step: d.step ?? x.step, percent: d.percent, status: 'running', waiting: false, waitingSince: null } : x));
        return;
      }
      // Send to edit (rc.9 fixes): a hand-off's step and percent patch the lists in place, as exports do.
      const handoffId = (d as unknown as { handoffId?: string }).handoffId;
      if (handoffId) {
        qc.setQueriesData<{ items: HandoffDTO[] }>({ queryKey: ['handoffs'] }, (old) => (old?.items
          ? { ...old, items: old.items.map((x) => (x.id === handoffId ? { ...x, status: 'running' as const, step: d.step ?? x.step, percent: d.percent } : x)) } : old));
        return;
      }
      if (d.assetId && d.percent != null) patchProgress(qc, d.assetId, d.step, d.percent);
    },
    dispose() {
      if (pending) cancel(pending);
      pending = null;
    },
  };
}

/** 1.2.4: how long before the next try to reconnect the live stream: 1, 2, 5 and 10 seconds, then every 30 seconds. */
const RECONNECT_DELAYS_MS = [1_000, 2_000, 5_000, 10_000];
export const reconnectDelay = (attempt: number): number => RECONNECT_DELAYS_MS[attempt] ?? 30_000;

/** What a cheap read of the session says before a retry: go on, stop (signed out: the app goes to sign-in), or wait. */
export type SessionCheck = 'ok' | 'signed-out' | 'unreachable';

export interface LiveReconnector {
  /** The stream said hello: the backoff starts over. */
  connected(): void;
  /** The stream closed and the browser will not retry it (an HTTP error, e.g. a 502 during a redeploy or a 401). */
  lost(): void;
  /** Back online or shown again: a stream that is down is retried now instead of at its next time. */
  now(): void;
  dispose(): void;
}

/**
 * Retries a closed live stream (1.2.4). An EventSource reconnects by itself only after a network error; after an HTTP
 * error it stays closed, so before 1.2.4 "Live updates are disconnected" stayed after every redeploy until a refresh.
 * Each try first reads the session (a GET never counts as activity): signed out, it stops; unreachable, it waits
 * for the next time; else it opens a new stream.
 */
export function liveReconnector(opts: {
  open(): void; checkSession(): Promise<SessionCheck>;
  schedule?: (fn: () => void, ms: number) => number; cancel?: (id: number) => void;
}): LiveReconnector {
  const schedule = opts.schedule ?? ((fn, ms) => window.setTimeout(fn, ms));
  const cancel = opts.cancel ?? ((id) => window.clearTimeout(id));
  let attempt = 0;
  let timer: number | null = null;
  let down = false;
  let trying = false;
  let done = false;
  const stopTimer = () => {
    if (timer !== null) cancel(timer);
    timer = null;
  };
  const wait = () => {
    if (timer === null && !done) timer = schedule(() => { timer = null; void retry(); }, reconnectDelay(attempt++));
  };
  const retry = async () => {
    if (done || trying) return;
    trying = true;
    const s = await opts.checkSession().catch((): SessionCheck => 'unreachable');
    trying = false;
    if (done) return;
    if (s === 'signed-out') { done = true; return; }
    if (s === 'unreachable') { wait(); return; }
    // Opening: up again unless the new stream fails too (then lost() comes, with the next delay).
    down = false;
    opts.open();
  };
  return {
    connected() {
      down = false;
      attempt = 0;
      stopTimer();
    },
    lost() {
      down = true;
      wait();
    },
    now() {
      if (!down || done || trying) return;
      stopTimer();
      void retry();
    },
    dispose() {
      done = true;
      stopTimer();
    },
  };
}

/** The session check before a retry; a 401 ends the session in the app (back to sign-in). */
async function checkLiveSession(qc: QueryClient): Promise<SessionCheck> {
  try {
    await api('/api/v1/me/session');
    return 'ok';
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      onSessionError(qc, err);
      return 'signed-out';
    }
    return 'unreachable';
  }
}

export function useLiveEvents(enabled: boolean): 'connecting' | 'live' | 'offline' {
  const qc = useQueryClient();
  const [state, setState] = useState<'connecting' | 'live' | 'offline'>('connecting');
  useEffect(() => {
    if (!enabled) return;
    const live = liveEventHandlers(qc);
    let es: EventSource | null = null;
    const open = () => {
      es?.close();
      const source = new EventSource('/api/v1/events');
      es = source;
      source.addEventListener('hello', () => {
        setState('live');
        reconnect.connected();
        live.hello();
      });
      // Notifications carry ids only; batched invalidation refetches what changed.
      source.addEventListener('domain', (e) => live.domain(JSON.parse((e as MessageEvent).data) as DomainData));
      source.addEventListener('progress', (e) => live.progress(JSON.parse((e as MessageEvent).data) as ProgressData));
      source.onerror = () => {
        // Still CONNECTING: the browser retries by itself (a network error). CLOSED: it gave up, so PRISM retries.
        if (source.readyState !== EventSource.CLOSED) { setState('connecting'); return; }
        setState('offline');
        reconnect.lost();
      };
    };
    const reconnect = liveReconnector({ open, checkSession: () => checkLiveSession(qc) });
    const retryNow = () => reconnect.now();
    const shown = () => { if (document.visibilityState === 'visible') reconnect.now(); };
    open();
    window.addEventListener('online', retryNow);
    document.addEventListener('visibilitychange', shown);
    return () => {
      window.removeEventListener('online', retryNow);
      document.removeEventListener('visibilitychange', shown);
      reconnect.dispose();
      es?.close();
      live.dispose();
    };
  }, [enabled, qc]);
  return state;
}
